/**
 * Web social sign-in (redirect flow).
 *
 * On the web the OAuth handshake is a full-page redirect, so the invite cannot
 * travel in the sign-in request body. Instead we run a pre-flight that sets an
 * HttpOnly `flavrly_invite` cookie the auth service reads when the provider
 * redirects back. The pre-flight MUST succeed before OAuth is launched -- a bad
 * invite has to stop the flow while we can still surface the error.
 */

import { authClient } from '@/config/auth-client';
import { ENV } from '@/config/env';
import type { SignInSocialOpts, SocialProvider } from './sign-in-social.types';

const INVITE_PREFLIGHT_PATH = '/api/auth/invite/preflight';

/** Where the provider redirects back to once OAuth completes. */
const getCallbackURL = (): string =>
  typeof window !== 'undefined' && window.location
    ? window.location.origin
    : ENV.AUTH_URL;

async function preflightInvite(inviteCode: string): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${ENV.AUTH_URL}${INVITE_PREFLIGHT_PATH}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ inviteCode }),
    });
  } catch {
    throw new Error(
      'We could not verify your invite. Check your connection and try again.'
    );
  }

  if (response.ok) {
    return;
  }

  let message = 'That invite code is not valid.';
  try {
    const data = (await response.json()) as { error?: string };
    if (data?.error) {
      message = data.error;
    }
  } catch {
    // Non-JSON error body -- fall back to the generic message.
  }
  throw new Error(message);
}

async function signInSocial(
  provider: SocialProvider,
  opts?: SignInSocialOpts
): Promise<void> {
  // Establish the invite cookie BEFORE the redirect; abort the whole flow if
  // the code is rejected.
  if (opts?.inviteCode) {
    await preflightInvite(opts.inviteCode);
  }

  const result = (await authClient.signIn.social({
    provider,
    callbackURL: getCallbackURL(),
  })) as { error?: { message?: string } } | undefined;

  if (result?.error) {
    throw new Error(result.error.message || 'Could not start social sign-in.');
  }
}

export function signInWithGoogle(opts?: SignInSocialOpts): Promise<void> {
  return signInSocial('google', opts);
}

export function signInWithApple(opts?: SignInSocialOpts): Promise<void> {
  return signInSocial('apple', opts);
}
